import {Component, Input, OnInit} from '@angular/core';
import {Observable} from 'rxjs';
import {NguCarouselConfig} from '@ngu/carousel';
import {HttpClient, HttpParams} from '@angular/common/http';
import Env from '../../../../../env/env';
import {map, startWith} from 'rxjs/operators';
import {VMAResult} from '../../estimate.component';
import {DecimalPipe} from '@angular/common';
import {ArtworkData, ArtworkDocument, SegmentationResult} from '../../../../elastic-utils';
import {Router} from '@angular/router';

interface NeighborsQuery {
  count: number;
  results: string[][];
  columns: [];
}

@Component({
  selector: 'app-result-overview',
  templateUrl: './result-overview.component.html',
  styleUrls: ['./result-overview.component.css']
})
export class ResultOverviewComponent implements OnInit {
  @Input() columns: string[];
  @Input() result: VMAResult;

  private readonly imageCDN = Env.imageCDN;
  public carouselTileItems$: Observable<ArtworkDocument[]>;
  public latestAuctions$: Observable<ArtworkDocument[]>;
  public carouselConfig: NguCarouselConfig = {
    grid: {xs: 1, sm: 1, md: 1, lg: 1, all: 0},
    point: {
      visible: true
    },
    touch: true,
    loop: true,
    // interval: {timing: 4000, initialDelay: 1000},
    animation: 'lazy'
  };

  constructor(private http: HttpClient, private decimalPipe: DecimalPipe, private router: Router) {
  }

  ngOnInit() {
    let params = new HttpParams();
    params = params.append('name', this.getCell('artwork_name'));
    params = params.append('date', this.getCell('artwork_date'));
    params = params.append('mediumId', this.getCell('mediums'));
    params = params.append('d1', this.getCell('d1', true));
    params = params.append('d2', this.getCell('d2', true));
    params = params.append('d3', this.getCell('d3', true));
    params = params.append('editionRank', this.getCell('edition_rank'));

    this.carouselTileItems$ = this.http.get<SegmentationResult>(Env.apiUrl + `/segmentation/${this.getCell('artist_id')}`, {params})
      .pipe(map(data => data.data), startWith([]));

    this.latestAuctions$ = this.http.get<SegmentationResult>(Env.apiUrl + `/latest-auctions/${this.getCell('artist_id')}`)
      .pipe(map(data => data.data), startWith([]));
  }

  getCell(columnName: string, raw: boolean = false): string {
    const source = {row: this.result.row, columns: this.columns};
    const idx = source.columns.indexOf(columnName);

    if (raw) {
      return source.row[idx];
    }

    if (idx === -1) {
      return '';
    }
    if (columnName === 'artist_liquidity_period' || columnName === 'artist_momentum' || columnName === 'artist_medium_liquidity' || columnName === 'artist_liquidity') {
      return Math.round(parseFloat(source.row[idx]) * 100).toString();
    }
    if (columnName === 'picture_url') {
      if (!source.row[idx]) {
        return 'na';
      }
      if (source.row[idx] && source.row[idx].match(/^https?:\/\//)) {
        return source.row[idx];
      }
      return Env.imageCDN + source.row[idx];
    }
    if (columnName === 'dob' && typeof source.row[idx] === 'string') {
      return source.row[idx].split('.')[0];
    }
    if (columnName === 'movements_analysis') {
      if (source.row[idx]) {
        return source.row[idx].replace(/\|/g, ', ');
      } else {
        return '';
      }
    }
    if (columnName === 'similarity_score') {
      const score = parseFloat(source.row[idx]);
      // tslint:disable-next-line:max-line-length
      if (score < 1) {
        return 'Very Bad';
      } else if (score < 2) {
        return 'Bad';
      } else if (score < 3) {
        return 'Average';
      } else if (score < 4) {
        return 'Good';
      } else {
        return 'Very Good';
      }
    }
    if (columnName === 'artwork_date') {
      if (this.getCell('has_artworkDate') === '0' || source.row[idx] === 'na') {
        return '';
      }
      return source.row[idx];
    }
    if (['d1', 'd2', 'd3'].indexOf(columnName) !== -1) {
      if (!source.row[idx] || source.row[idx] === 'na' || source.row[idx] === 'null') {
        return 'na';
      }
      return this.decimalPipe.transform(source.row[idx], '1.0-0');
    }
    if (columnName.startsWith('usd_estimation_')) {
      return this.flattenEstimation(parseInt(source.row[idx], 10));
    }
    if (columnName === 'average_usd_price_of_the_artist_for_over_period_this_medium') {
      return this.decimalPipe.transform(source.row[idx], '1.0-0');
    }
    if (columnName === 'average_growth_rate_per_medium_this_artist') {
      const val = parseFloat(source.row[idx]);
      if (!isNaN(val))
        return (val > 1 ? '+' : '') + this.decimalPipe.transform((val * 100) - 100, '1.0-0');
      else
        return 'na';
    }
    if (!source.row[idx]) {
      return '';
    }
    return source.row[idx];
  }

  getEstimation(): string {
    let results = [];
    //const modelNo = parseInt(this.getCell('segmentation_price'), 10) >= 3 ? 1 : 0;
    const modelNo = 0;
    if (!this.result.ml.priceError) {
      results.push({price: this.result.ml.price[Object.keys(this.result.ml.price)[modelNo]], delta: Math.abs(this.result.price - this.result.ml.price[Object.keys(this.result.ml.price)[modelNo]])});
    }
    if (!this.result.comparable.priceError) {
      results.push({price: this.result.comparable.price, delta: Math.abs(this.result.price - this.result.comparable.price)});
    }
    if (!results.length) {
      return null;
    }
    results = results.sort((a, b) => a.delta - b.delta);
    const price = results[0].price;
    return `${this.flattenEstimation(price - price * 0.05)} - ${this.flattenEstimation(price + price * 0.05)}`;
  }

  flattenEstimation(i: number): string {
    const l = Math.floor(Math.log10(i));
    const p = Math.pow(10, l - 1);
    const s = Math.round(i / Math.pow(10, l));
    const f = s > 2 ? s > 5 ? 5 : 2 : 1;
    const price = Math.round((i / p) / f) * f * p;
    return this.decimalPipe.transform(price, '1.0-0');
  }

  estimateRelated(artwork: ArtworkDocument) {
    this.router.navigate(['/search/artworks/', artwork._source.artistId], {
      fragment: btoa(escape(JSON.stringify(artwork)))
    });
  }
}
